import {getUserDefinedObjectAdditionalColumnDetails} from "~/domainModels/userDefinedObjectAdditionalColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import {EmotoradEventContainerTypes} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/emotorad/EventContainers";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronUpIcon from "@atlaskit/icon/core/chevron-up";
import {KanbanCardDispositionControl} from "~/components/ottopilotDataViewer/kanbanCardDispositionControl";
import LocationIcon from "@atlaskit/icon/core/location";
import OfficeBuildingIcon from "@atlaskit/icon/core/office-building";
import PeopleGroupIcon from "@atlaskit/icon/core/people-group";
import {KanbanCardRowActions} from "~/components/ottopilotDataViewer/rowEngagement/KanbanCardRowActions";
import {resolveKanbanPhoneDial} from "~/components/ottopilotDataViewer/rowEngagement/resolveKanbanPhoneDial";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import SubtasksIcon from "@atlaskit/icon/core/subtasks";
import {token} from "@atlaskit/tokens";
import {DateTime} from "luxon";
import {useMemo, useState, type ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {ModelVariantIcon} from "~/components/svgs";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";

import {
    EmotoradEventTypes,
    EmotoradEventContainerStatusEnum,
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID,
    EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID,
    EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID,
} from "~/domainModels/workspaceSpecific/emotorad";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeTooltip} from "@vesta/ananke/components/anankeTooltip";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import type {BtlActivity} from "~/domainModels/btlActivities";

const LIFECYCLE_KANBAN_EVENT_ROWS: Array<{templateId: Uuid; label: string}> = [
    {templateId: EmotoradEventTypes.lead as Uuid, label: "Leads"},
    {templateId: EmotoradEventTypes.testride as Uuid, label: "Test rides"},
    {templateId: EmotoradEventTypes.sales as Uuid, label: "Sales"},
    {templateId: EmotoradEventTypes.call as Uuid, label: "Calls"},
    {templateId: EmotoradEventTypes.walkin as Uuid, label: "Walk-ins"},
];

function formatLifecycleLastActivityLine(journey: EventContainerWithEvents): string {
    const rawOn = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY_ON__COLUMN_ID];
    const rawAt = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LAST_ACTIVITY__COLUMN_ID];
    const iso =
        rawOn !== null && rawOn !== undefined && String(rawOn).trim() !== ""
            ? String(rawOn)
            : rawAt !== null && rawAt !== undefined && String(rawAt).trim() !== ""
              ? String(rawAt)
              : journey.updatedAt;
    if (iso === "") {
        return "-";
    }
    const dt = DateTime.fromISO(iso, {zone: "Asia/Kolkata"});
    if (dt.isValid !== true) {
        return "-";
    }
    return `${dt.toFormat("d LLL yyyy")}, ${dt.toFormat("h:mm a").toLowerCase()}`;
}

function resolveLifecycleCount(journey: EventContainerWithEvents): string {
    const primary = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__LIFE_CYCLE_COUNT__COLUMN_ID];
    if (primary !== null && primary !== undefined && String(primary).trim() !== "") {
        return String(primary);
    }
    const fallbackJourneyCount = journey.additionalColumnValues[EMOTORAD__CUSTOMERS__EVENT_CONTAINER_COUNT_COLUMN_ID];
    if (fallbackJourneyCount !== null && fallbackJourneyCount !== undefined && String(fallbackJourneyCount).trim() !== "") {
        return String(fallbackJourneyCount);
    }
    const fallbackLive = journey.additionalColumnValues[EMOTORAD__CUSTOMERS__LIVE_EVENT_CONTAINER_COUNT_COLUMN_ID];
    if (fallbackLive !== null && fallbackLive !== undefined && String(fallbackLive).trim() !== "") {
        return String(fallbackLive);
    }
    return "-";
}

type FieldCellProps = {
    icon: ReactNode;
    label: string;
    value: string;
};

function FieldCell({icon, label, value}: FieldCellProps) {
    return (
        <div>
            <div
                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 grid grid-cols-[max-content_max-content] items-center gap-1"
                style={{color: token("color.text.subtlest")}}
            >
                {icon}
                <span>{label}</span>
            </div>
            <div className="grid grid-cols-[max-content_1fr] items-center gap-1">
                <div className="invisible">{icon}</div>
                <div
                    className="text-[0.875rem] leading-ds-space-250 line-clamp-1 text-ellipsis font-ds-font-weight-medium"
                    style={{color: token("color.text")}}
                >
                    {value}
                </div>
            </div>
        </div>
    );
}

export function EmotoradLifecycleKanbanBoardCard({
    journey,
    rowActionsConfig,
    onDispositionClick,
}: {
    journey: EventContainerWithEvents;
    rowActionsConfig: TableRowActionsConfig<EventContainerWithEvents>;
    onDispositionClick?: () => void;
}) {
    const getAdditionalColumns = useCapabilityUdoColumns();
    const journeysModuleConfiguration = useRequiredEventContainerTemplate(EmotoradEventContainerTypes.Journeys as Uuid);
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const btlActivities = useBtlActivities() as Array<BtlActivity>;
    const eventTemplates = useEventTemplates();
    const products = useProductCatalogue() ?? [];

    const callColumnId =
        journeysModuleConfiguration.configuration.eventContainersPage.callButtonColumnId ?? EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID;

    const getColumnDisplay = useMemo(() => {
        return (columnId: Uuid): string => {
            const rawValue = journey.additionalColumnValues[columnId];
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                return "-";
            }
            const configuredColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.id === columnId);
            if (configuredColumn === undefined) {
                if (columnId === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID && typeof rawValue === "string") {
                    const ou = organizationalUnits.find((unit) => unit.id === rawValue);
                    if (ou !== undefined) {
                        const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                        if (ouTemplate !== undefined) {
                            return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                        }
                    }
                    return rawValue;
                }
                return String(rawValue);
            }
            if (configuredColumn.id === EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID && typeof rawValue === "string") {
                const ou = organizationalUnits.find((unit) => unit.id === rawValue);
                if (ou !== undefined) {
                    const ouTemplate = organizationalUnitTemplates.find((template) => template.id === ou.templateId);
                    if (ouTemplate !== undefined) {
                        return processOttopilotStringTemplate(ouTemplate.instanceDisplayNameTemplate, ou.additionalColumnValues);
                    }
                }
            }
            return getUserDefinedObjectAdditionalColumnDetails(configuredColumn).stringValueGetter({
                value: rawValue as never,
                users: users,
                userTemplates: userTemplates,
                organizationalUnits: organizationalUnits,
                organizationalUnitTemplates: organizationalUnitTemplates,
                products: products,
            });
        };
    }, [getAdditionalColumns, journey, journeysModuleConfiguration, organizationalUnits, organizationalUnitTemplates, users, userTemplates, btlActivities, eventTemplates, products]);

    const customerName = processOttopilotStringTemplate(journeysModuleConfiguration.configuration.displayNameTemplate, journey.additionalColumnValues);
    const contactRaw = journey.additionalColumnValues[callColumnId as Uuid];
    const contactDisplay = getColumnDisplay(callColumnId as Uuid);
    const kanbanPhoneDial = useMemo(
        () => resolveKanbanPhoneDial(contactRaw, contactDisplay !== "-" ? contactDisplay : undefined),
        [contactRaw, contactDisplay],
    );
    const pincode = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID);
    const modelVariant = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__MODEL_VARIANT__COLUMN_ID);
    const assignedDealer = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID);
    const lifecycleCount = resolveLifecycleCount(journey);
    const lastActivityLabel = formatLifecycleLastActivityLine(journey);

    const dispositionTooltip = useMemo(() => {
        const l1 = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID);
        const l2 = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID);
        const l3 = getColumnDisplay(EMOTORAD__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID);
        const parts = [l1, l2, l3].filter((p) => p !== "-" && p.trim() !== "");
        if (parts.length === 0) {
            return "";
        }
        return `Dispositions: ${parts.join(" | ")}`;
    }, [getColumnDisplay]);

    const eventsSummary = useMemo(() => {
        return LIFECYCLE_KANBAN_EVENT_ROWS.map((row) => ({
            ...row,
            count: journey.events.filter((event) => event.templateId === row.templateId).length,
        }));
    }, [journey.id, journey.events]);

    const [eventsOpen, setEventsOpen] = useState(false);
    const [hovered, setHovered] = useState(false);

    const lifecycleStatusRaw = journey.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__STATUS__COLUMN_ID];
    const isLiveLifecycleJourney = String(lifecycleStatusRaw) === String(EmotoradEventContainerStatusEnum.LIVE);

    return (
        <div
            className="relative"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div
                className={twJoin("w-full shadow-sm relative group")}
                style={{
                    borderRadius: token("radius.small"),
                    background: hovered ? token("color.background.disabled") : token("color.icon.inverse"),
                    boxShadow: "0 0 1px 0 rgba(30, 31, 33, 0.12), 0 0 8px 0 rgba(30, 31, 33, 0.16)",
                }}
            >
                <div className="w-full pt-ds-space-200 px-ds-space-200 pb-ds-space-150">
                    <div
                        className="grid grid-flow-col gap-ds-space-100 justify-between items-start"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="line-clamp-1 overflow-hidden min-w-0">
                            <div
                                className="text-[1rem] font-ds-font-weight-bold leading-ds-space-300 line-clamp-1 text-ellipsis"
                                style={{fontFamily: "Inter", color: token("color.text")}}
                            >
                                {customerName}
                            </div>
                        </div>
                        <KanbanCardRowActions
                            row={journey}
                            config={rowActionsConfig}
                            phoneDial={kanbanPhoneDial}
                        />
                    </div>

                    <VerticalSpacer className="h-ds-space-100" />

                    <div className="flex flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-050 justify-between">
                        <div className="grid grid-flow-col justify-start gap-x-ds-space-075 items-center min-w-0">
                            <CalendarIcon
                                label=""
                                size="small"
                                color={token("color.icon.subtle")}
                            />
                            <span
                                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200"
                                style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                            >
                                {lastActivityLabel}
                            </span>
                            {onDispositionClick !== undefined ? (
                                <KanbanCardDispositionControl
                                    label=""
                                    tooltip={dispositionTooltip !== "" ? dispositionTooltip : undefined}
                                    onClick={onDispositionClick}
                                />
                            ) : dispositionTooltip !== "" ? (
                                <AnankeTooltip
                                    content={dispositionTooltip}
                                    position="bottom"
                                >
                                    {(tooltipProps) => (
                                        <span
                                            {...tooltipProps}
                                            className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 line-clamp-1"
                                            style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                                        >
                                            Disposition
                                        </span>
                                    )}
                                </AnankeTooltip>
                            ) : null}
                        </div>
                    </div>

                    <VerticalSpacer className="h-ds-space-150" />
                    <div className="w-full h-px bg-ds-border" />
                    <VerticalSpacer className="h-ds-space-150" />

                    <div className="grid grid-cols-2 gap-x-ds-space-250 gap-y-ds-space-150">
                        <FieldCell
                            icon={
                                <LocationIcon
                                    label=""
                                    size="small"
                                    color={token("color.icon.subtle")}
                                />
                            }
                            label="Pincode"
                            value={pincode}
                        />
                        <FieldCell
                            icon={
                                <ModelVariantIcon
                                    className="w-4 h-4"
                                    style={{color: token("color.icon.subtle")}}
                                />
                            }
                            label="Model Variant"
                            value={modelVariant}
                        />
                        <FieldCell
                            icon={
                                <PeopleGroupIcon
                                    label=""
                                    size="small"
                                    color={token("color.icon.subtle")}
                                />
                            }
                            label="Lifecycle count"
                            value={lifecycleCount}
                        />
                        <div className="col-span-2">
                            <FieldCell
                                icon={
                                    <OfficeBuildingIcon
                                        label=""
                                        size="small"
                                        color={token("color.icon.subtle")}
                                    />
                                }
                                label="Assigned Dealer"
                                value={assignedDealer}
                            />
                        </div>
                    </div>

                    <VerticalSpacer className="h-ds-space-100" />
                </div>
                <div className="w-full h-px bg-ds-border" />
                <div className="w-full px-ds-space-200 py-ds-space-150">
                    <div className="grid grid-cols-[auto_auto] gap-x-ds-space-150 justify-between items-center">
                        <div className="flex gap-ds-space-075 items-center min-w-0">
                            <SubtasksIcon
                                size="small"
                                label=""
                            />
                            <span
                                className="text-[0.75rem] font-ds-font-weight-medium leading-ds-space-200 truncate"
                                style={{fontFamily: "Inter", color: token("color.text.subtle")}}
                            >
                                {journey.events.length} Events
                            </span>
                        </div>
                        <AnankeIconButton
                            type="button"
                            spacing="compact"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEventsOpen((prev) => !prev);
                            }}
                            icon={eventsOpen ? ChevronUpIcon : ChevronDownIcon}
                            label={eventsOpen ? "Collapse events" : "Expand events"}
                            appearance="subtle"
                        />
                    </div>
                    <div
                        className={twJoin("grid transition-[grid-template-rows] duration-300 ease-in-out", eventsOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
                    >
                        <div className="overflow-hidden">
                            <div className="grid grid-cols-2 pt-ds-space-100 gap-ds-space-075 w-full">
                                <ItemBuilder
                                    items={eventsSummary}
                                    itemBuilder={(row, index) => (
                                        <div
                                            key={row.templateId}
                                            className="rounded-md grid grid-flow-col grid-cols-[1fr_auto] gap-x-ds-space-075 justify-between py-ds-space-075 px-ds-space-075 items-center min-w-0"
                                            style={{
                                                fontFamily: "Inter",
                                                color: token("color.text.subtle"),
                                                background: token("color.background.disabled"),
                                            }}
                                        >
                                            <span className="grid grid-flow-col auto-cols-max gap-x-ds-space-075 items-center min-w-0">
                                                {index === 0 && isLiveLifecycleJourney === true ? (
                                                    <span
                                                        className="inline-block rounded-full shrink-0"
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            background: token("color.icon.success"),
                                                        }}
                                                    />
                                                ) : null}
                                                <span
                                                    className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 line-clamp-1 text-ellipsis min-w-0"
                                                    style={{color: token("color.text")}}
                                                >
                                                    {row.label}
                                                </span>
                                            </span>
                                            <span
                                                className="text-[0.875rem] font-ds-font-weight-medium leading-ds-space-250 shrink-0"
                                                style={{color: token("color.text")}}
                                            >
                                                {row.count}
                                            </span>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
